import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rfiApi, tecniciApi } from '../services/api'
import toast from 'react-hot-toast'
import { Plus, Trash2 } from 'lucide-react'

interface TecnicoOption { id: number; nome: string; cognome: string }
interface OraRiga { tecnico_id: number; ore: number }
interface MaterialeRiga { descrizione_libera: string; quantita: number; unita_misura_libera?: string }
interface Giornata {
  id: number
  data: string
  note?: string
  ore: (OraRiga & { id: number })[]
  materiali: (MaterialeRiga & { id: number })[]
}
interface RapportinoDettaglio {
  id: number
  odl_id: number
  stato: string
  guerrato_id?: number
  giornate: Giornata[]
}

function oggiIso() {
  return new Date().toISOString().slice(0, 10)
}

export default function RfiRapportinoDetail() {
  const { id: odlId } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: ordine } = useQuery({
    queryKey: ['rfi-ordine', odlId],
    queryFn: () => rfiApi.getOrdine(Number(odlId)).then(r => r.data),
    enabled: !!odlId,
  })

  const rapportinoId = ordine?.rapportino_id as number | undefined

  const { data: rapportino, isLoading } = useQuery({
    queryKey: ['rfi-rapportino', rapportinoId],
    queryFn: () => rfiApi.getRapportino(rapportinoId as number).then(r => r.data as RapportinoDettaglio),
    enabled: !!rapportinoId,
  })

  const { data: tuttiTecnici = [] } = useQuery({
    queryKey: ['tecnici-lista-rfi'],
    queryFn: () => tecniciApi.list({ per_page: 100 }).then((r: any) => (r.data.items || r.data) as TecnicoOption[]),
  })

  const tecniciIdsOrdine = (ordine?.tecnici_ids as number[] | undefined) || []
  const tecnici = tecniciIdsOrdine.length > 0
    ? tuttiTecnici.filter(t => tecniciIdsOrdine.includes(t.id))
    : tuttiTecnici

  const [data, setData] = useState(oggiIso())
  const [ore, setOre] = useState<OraRiga[]>([])
  const [materiali, setMateriali] = useState<MaterialeRiga[]>([])

  function tecnicoNome(id: number): string {
    const t = tuttiTecnici.find(t => t.id === id)
    if (!t) return `Tecnico #${id}`
    return `${t.nome} ${t.cognome}`
  }

  const salvaMut = useMutation({
    mutationFn: () => rfiApi.salvaGiornata(rapportinoId as number, { data, ore, materiali }),
    onSuccess: () => {
      toast.success('Giornata salvata')
      qc.invalidateQueries({ queryKey: ['rfi-rapportino', rapportinoId] })
      setOre([])
      setMateriali([])
    },
    onError: () => toast.error('Errore salvataggio giornata'),
  })

  const chiudiMut = useMutation({
    mutationFn: () => rfiApi.chiudiRapportino(rapportinoId as number),
    onSuccess: (r) => {
      toast.success('Attività chiusa: Rapportino Guerrato generato')
      navigate(`/rfi/rapportino-guerrato/${r.data.rapportino_guerrato_id}`)
    },
    onError: () => toast.error('Errore in chiusura'),
  })

  if (isLoading || !rapportino) return <div className="text-center py-16 text-slate-400">Caricamento...</div>

  const soloLettura = rapportino.stato === 'chiuso'

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Rapportino — {ordine?.oggetto}</h1>
        {!soloLettura && (
          <button className="btn-primary" disabled={rapportino.giornate.length === 0 || chiudiMut.isPending} onClick={() => chiudiMut.mutate()}>
            {chiudiMut.isPending ? 'Chiusura...' : 'Fine Attività'}
          </button>
        )}
        {soloLettura && rapportino.guerrato_id && (
          <button className="btn-primary" onClick={() => navigate(`/rfi/rapportino-guerrato/${rapportino.guerrato_id}`)}>
            Vedi Rapportino Guerrato / Ristampa PDF
          </button>
        )}
      </div>

      {!soloLettura && (
        <div className="card p-4 space-y-3">
          <h2 className="font-medium">Aggiungi/aggiorna giornata</h2>
          <input type="date" className="input" value={data} onChange={e => setData(e.target.value)} />

          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase">Ore per tecnico</p>
            {ore.map((r, i) => (
              <div key={i} className="flex gap-2 items-center">
                <select className="select" value={r.tecnico_id} onChange={e => {
                  const nuove = [...ore]; nuove[i] = { ...r, tecnico_id: Number(e.target.value) }; setOre(nuove)
                }}>
                  <option value={0}>Seleziona tecnico</option>
                  {tecnici.map(t => <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>)}
                </select>
                <input type="number" className="input w-24" value={r.ore} onChange={e => {
                  const nuove = [...ore]; nuove[i] = { ...r, ore: Number(e.target.value) }; setOre(nuove)
                }} />
                <button className="btn-ghost" onClick={() => setOre(ore.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
              </div>
            ))}
            <button className="btn-secondary text-xs" onClick={() => setOre([...ore, { tecnico_id: 0, ore: 0 }])}>
              <Plus size={12} className="inline mr-1" /> Aggiungi tecnico
            </button>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase">Materiali usati</p>
            {materiali.map((m, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input className="input flex-1" placeholder="Descrizione materiale" value={m.descrizione_libera} onChange={e => {
                  const nuovi = [...materiali]; nuovi[i] = { ...m, descrizione_libera: e.target.value }; setMateriali(nuovi)
                }} />
                <input type="number" className="input w-24" placeholder="Quantità" value={m.quantita} onChange={e => {
                  const nuovi = [...materiali]; nuovi[i] = { ...m, quantita: Number(e.target.value) }; setMateriali(nuovi)
                }} />
                <input className="input w-20" placeholder="UM" value={m.unita_misura_libera || ''} onChange={e => {
                  const nuovi = [...materiali]; nuovi[i] = { ...m, unita_misura_libera: e.target.value }; setMateriali(nuovi)
                }} />
                <button className="btn-ghost" onClick={() => setMateriali(materiali.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
              </div>
            ))}
            <button className="btn-secondary text-xs" onClick={() => setMateriali([...materiali, { descrizione_libera: '', quantita: 0 }])}>
              <Plus size={12} className="inline mr-1" /> Aggiungi materiale
            </button>
          </div>

          <button
            className="btn-primary"
            disabled={salvaMut.isPending || ore.some(r => r.tecnico_id === 0)}
            onClick={() => {
              if (ore.some(r => r.tecnico_id === 0)) {
                toast.error('Seleziona un tecnico per ogni riga ore')
                return
              }
              salvaMut.mutate()
            }}
          >
            {salvaMut.isPending ? 'Salvataggio...' : 'Salva giornata'}
          </button>
        </div>
      )}

      <div className="space-y-2">
        <h2 className="font-medium">Giornate registrate</h2>
        {rapportino.giornate.map(g => (
          <div key={g.id} className="card p-3 text-sm">
            <div className="font-semibold">{g.data}</div>
            {g.ore.map(o => <div key={o.id} className="text-slate-600">{tecnicoNome(o.tecnico_id)}: {o.ore}h</div>)}
            {g.materiali.map(m => <div key={m.id} className="text-slate-600">{m.descrizione_libera} — {m.quantita} {m.unita_misura_libera}</div>)}
          </div>
        ))}
      </div>
    </div>
  )
}
